import { useTranslation } from 'react-i18next'
import { AlertTriangle, Check, ListFilter, Plus, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SearchSelect } from '@/components/ui/search-select'
import { OPERATORS, isAudienceType, type useAudienceFields } from './audienceFields'
import { MAX_AUDIENCE_FIELD_CONDITIONS, type AudienceFieldCondition, type AudienceFieldOperator, type AudienceFieldWarning } from '@/types/campaigns'

const chip = (active: boolean, disabled?: boolean) =>
  cn(
    'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
    active ? 'bg-primary text-white' : 'bg-muted text-foreground hover:bg-muted/70',
    disabled && 'pointer-events-none opacity-60'
  )

interface AudienceFieldConditionsProps {
  value: AudienceFieldCondition[]
  onChange: (value: AudienceFieldCondition[]) => void
  fields: ReturnType<typeof useAudienceFields>
  /** How many more conditions fit (the limit counts the inclusion and the exclusion together). */
  room: number
  /** The warnings of this side (`scope` already matched), by the condition's position. */
  warnings?: AudienceFieldWarning[]
  disabled?: boolean
  idPrefix: string
}

/** F-156: "Campos" in a side of the audience: one row per condition (field, operator, options). */
export const AudienceFieldConditions = ({ value, onChange, fields, room, warnings = [], disabled, idPrefix }: AudienceFieldConditionsProps) => {
  const { t } = useTranslation('campaigns')
  const set = (index: number, next: AudienceFieldCondition) => onChange(value.map((c, i) => (i === index ? next : c)))
  const remove = (index: number) => onChange(value.filter((_, i) => i !== index))
  const add = () => onChange([...value, { fieldId: '', operator: 'oneOf', values: [] }])

  const warningText = (w: AudienceFieldWarning, operator: AudienceFieldOperator) => {
    if (w.reason === 'optionRemoved') {
      const values = (w.removedValues ?? []).join(', ')
      return operator === 'containsAll' ? t('audience.fields.warnings.optionRemovedAll', { values }) : t('audience.fields.warnings.optionRemoved', { values })
    }
    return ['fieldNotFound', 'fieldClosed', 'fieldTypeChanged'].includes(w.reason)
      ? t(`audience.fields.warnings.${w.reason}`)
      : t('audience.fields.warnings.unknown')
  }

  return (
    <div className="space-y-2" role="group" aria-labelledby={`${idPrefix}-fields`} data-testid={`${idPrefix}-fields`}>
      <h4 id={`${idPrefix}-fields`} className="flex items-center gap-2 text-sm font-medium text-foreground">
        <ListFilter className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        {t('audience.fields.title')}
      </h4>

      {fields.usable.length === 0 && value.length === 0 ? (
        <p className="text-sm text-muted-foreground">{fields.noAccess ? t('audience.noAccess') : t('audience.fields.none')}</p>
      ) : (
        <>
          {value.map((condition, index) => {
            const field = fields.all.find((f) => f.id === condition.fieldId || (!condition.fieldId && f.key === condition.key))
            const type = field && isAudienceType(field.type) ? field.type : null
            const warning = warnings.find((w) => w.index === index)
            /* A saved condition on a field that is no longer offered keeps its name in the picker. */
            const options = [...fields.usable, ...(field && !fields.usable.includes(field) ? [field] : [])].map((f) => ({
              value: f.id,
              label: f.label,
              description: isAudienceType(f.type) ? t(`audience.fields.types.${f.type}`) : undefined,
            }))
            return (
              <div key={index} className="space-y-3 rounded-lg border border-border p-3" data-testid={`${idPrefix}-field-condition`}>
                <div className="flex items-start gap-2">
                  <SearchSelect
                    className="flex-1"
                    value={field?.id ?? null}
                    onChange={(id) => {
                      const next = fields.all.find((f) => f.id === id)
                      if (!next || !isAudienceType(next.type)) return
                      set(index, { fieldId: next.id, key: next.key, operator: OPERATORS[next.type][0], values: [] })
                    }}
                    options={options}
                    placeholder={condition.fieldId || condition.key ? (condition.key ?? t('audience.fields.unknownField')) : t('audience.fields.pickField')}
                    searchPlaceholder={t('audience.fields.searchField')}
                    emptyLabel={t('audience.fields.noMatch')}
                    disabled={disabled}
                    aria-label={t('audience.fields.pickField')}
                    testId={`${idPrefix}-field-select-${index}`}
                  />
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    disabled={disabled}
                    aria-label={t('audience.fields.remove')}
                    title={t('audience.fields.remove')}
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-60"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>

                {type && (
                  <div className="flex flex-wrap items-center gap-2" role="radiogroup" aria-label={t('audience.fields.operatorLabel')}>
                    {type === 'SELECT' ? (
                      <span className="text-sm text-muted-foreground">{t('audience.fields.operators.oneOf')}</span>
                    ) : OPERATORS[type].map((op) => (
                      <button key={op} type="button" role="radio" aria-checked={condition.operator === op} className={chip(condition.operator === op, disabled)}
                        onClick={() => set(index, { ...condition, operator: op })}>
                        {condition.operator === op && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
                        {t(`audience.fields.operators.${op}`)}
                      </button>
                    ))}
                  </div>
                )}

                {type && type !== 'BOOLEAN' && (
                  <div className="flex flex-wrap gap-2" role="group" aria-label={t('audience.fields.valuesLabel', { field: field?.label ?? '' })}>
                    {(field?.options ?? []).map((option) => {
                      const on = condition.values.includes(option.value)
                      return (
                        <button key={option.value} type="button" aria-pressed={on} className={chip(on, disabled)}
                          onClick={() => set(index, { ...condition, values: on ? condition.values.filter((v) => v !== option.value) : [...condition.values, option.value] })}>
                          {on && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
                          {option.label}
                        </button>
                      )
                    })}
                  </div>
                )}

                {warning && (
                  <p className="flex items-start gap-2 rounded-lg bg-warning-surface p-2 text-xs text-warning-text" role="status" data-testid={`${idPrefix}-field-warning`}>
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                    {warningText(warning, condition.operator)}
                  </p>
                )}
              </div>
            )
          })}

          {fields.usable.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <button type="button" onClick={add} disabled={disabled || room <= 0}
                className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-primary hover:bg-primary-50 disabled:pointer-events-none disabled:opacity-50"
                data-testid={`${idPrefix}-field-add`}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                {t('audience.fields.add')}
              </button>
              {room <= 0 && <span className="text-xs text-muted-foreground">{t('audience.fields.limit', { max: MAX_AUDIENCE_FIELD_CONDITIONS })}</span>}
            </div>
          )}
        </>
      )}
    </div>
  )
}
